/**
 * PHARMAKON — the print root (refonte WP-19 · F4, design/ux/01_foundations.md §3.16, AUD-UXDS-10).
 *
 * A document prints from a dedicated, hidden iframe that holds ONLY its `[data-print-doc]` node,
 * the print stylesheet, the app's own @font-face rules and the `@page` rule its page key asks for
 * (A4 portrait / landscape with the « Page X / Y » counters and the « Imprimé le … par … » line in
 * the page margin boxes, or a 40 × 25 / 50 × 30 mm thermal label with no margin). The app shell is
 * never part of the printed document; `styles/print.css` hides it as a safety net when the browser
 * prints the whole page anyway.
 *
 * Escaping: the document body is React-rendered markup (React escapes every user string); every
 * string this module writes itself — the window title, the footer line inside the CSS `content`
 * strings — goes through `escapeHtml` / `cssString`, so no user value can close the `<title>` or
 * the `<style>` element.
 *
 * Pure builders first (unit-tested in the node environment), the DOM orchestration last, written
 * against an injected `document` so it is testable without a browser.
 */
import { PRINT_FRAME, PRINT_LABELS, PRINT_KINDS, printPage } from "@/config/print.config.js";

/** Escape a string for HTML text or a double-quoted attribute. */
export function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
  );
}

/**
 * A CSS string literal (double-quoted). Backslash, quotes, line breaks and `<` / `>` are written as
 * CSS escapes, so the value can neither end the string nor close the surrounding `<style>`.
 */
export function cssString(value) {
  const body = String(value ?? "").replace(/[\\"'<>\n\r\f\u2028\u2029]/g, (c) => {
    return `\\${c.charCodeAt(0).toString(16)} `;
  });
  return `"${body}"`;
}

const pad2 = (n) => String(n).padStart(2, "0");

/** « 06/10/2026 à 14:32 » (local time). */
export function formatPrintDateTime(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${PRINT_LABELS.a} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** « Imprimé le 06/10/2026 à 14:32 par Yacine Dait » — the A4 footer line (ux/01 §3.16). */
export function footerLine({ printedAt, printedBy } = {}) {
  const when = formatPrintDateTime(printedAt ?? new Date());
  const who = String(printedBy ?? "").trim();
  return `${PRINT_LABELS.imprimeLe} ${when}${who ? ` ${PRINT_LABELS.par} ${who}` : ""}`;
}

/**
 * The `@page` rule of a page key. A4: the ux/01 §3.16 margins, « Page X / Y » through the CSS page
 * counters in the bottom-right margin box and the print line bottom-left. Label: the roll size, no
 * margin (one label per page, D-16).
 */
export function pageCss(pageKey, { printedAt, printedBy } = {}) {
  const p = printPage(pageKey);
  const m = p.margin_mm;
  const margin = `${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm`;
  if (p.kind !== PRINT_KINDS.A4 || !p.footer) {
    return `@page { size: ${p.size}; margin: ${margin}; }`;
  }
  const box = "font-family: 'Public Sans', Arial, sans-serif; font-size: 8pt; color: #000; vertical-align: top; padding-top: 2mm;";
  return (
    `@page { size: ${p.size}; margin: ${margin};` +
    ` @bottom-left { content: ${cssString(footerLine({ printedAt, printedBy }))}; ${box} }` +
    ` @bottom-right { content: ${cssString(`${PRINT_LABELS.page} `)} counter(page) " / " counter(pages); ${box} text-align: right; } }`
  );
}

/** Resolve the `url(...)` references of a CSS rule text against the sheet that declared it. */
export function absolutizeCssUrls(cssText, baseHref) {
  if (!baseHref) return cssText;
  return String(cssText).replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (whole, q, u) => {
    if (/^(data:|blob:|https?:|#)/i.test(u)) return whole;
    try {
      return `url("${new URL(u, baseHref).href}")`;
    } catch {
      return whole;
    }
  });
}

const FONT_FACE_RULE = 5; // CSSRule.FONT_FACE_RULE

/**
 * The app's @font-face rules (Public Sans, IBM Plex Mono — self-hosted, WP-14) as one CSS text, URLs
 * made absolute so they load inside the frame. A sheet that refuses `cssRules` (cross-origin) is
 * skipped: the print falls back to the stack in print.css.
 *
 * @param {{ styleSheets?: ArrayLike<any>, baseURI?: string }} doc
 */
export function collectFontFaceCss(doc) {
  const out = [];
  const sheets = Array.from(doc?.styleSheets ?? []);
  for (const sheet of sheets) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    for (const rule of Array.from(rules ?? [])) {
      if (rule.type === FONT_FACE_RULE || /^@font-face/i.test(rule.cssText ?? "")) {
        out.push(absolutizeCssUrls(rule.cssText, sheet.href || doc.baseURI));
      }
    }
  }
  return out.join("\n");
}

/**
 * The complete HTML of the print frame.
 *
 * @param {{ title?: string, bodyHtml: string, css?: string, fontCss?: string, pageKey?: string,
 *           printedAt?: Date, printedBy?: string, lang?: string }} input
 */
export function buildPrintHtml({ title, bodyHtml, css = "", fontCss = "", pageKey, printedAt, printedBy, lang = "fr" }) {
  const p = printPage(pageKey);
  const styles = [fontCss, css, pageCss(p.cle, { printedAt, printedBy })]
    .filter(Boolean)
    .join("\n")
    // a stylesheet can never contain the end tag of its own element
    .replace(/<\/style/gi, "<\\/style");
  return (
    `<!doctype html><html lang="${escapeHtml(lang)}"><head><meta charset="utf-8">` +
    `<title>${escapeHtml(title || PRINT_LABELS.documentSansTitre)}</title>` +
    `<style>${styles}</style></head>` +
    `<body class="pk-print-body pk-print-body--${escapeHtml(p.kind)}" data-print-page="${escapeHtml(p.cle)}">` +
    `${bodyHtml}</body></html>`
  );
}

/** Read what a `[data-print-doc]` node declares about itself. */
export function readPrintDoc(node) {
  if (!node) throw printError("PRINT_DOC_ABSENT");
  const doc = node.matches?.("[data-print-doc]") ? node : node.querySelector?.("[data-print-doc]");
  if (!doc) throw printError("PRINT_DOC_ABSENT");
  return {
    html: doc.outerHTML,
    pageKey: doc.getAttribute("data-print-page") || undefined,
    title: doc.getAttribute("data-print-title") || undefined,
  };
}

function printError(code, cause) {
  const e = new Error(code);
  e.code = code;
  if (cause) e.cause = cause;
  return e;
}

/** Wait for a promise, but never longer than `ms`. */
function withTimeout(promise, ms, setTimer) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    setTimer(finish, ms);
    Promise.resolve(promise).then(finish, finish);
  });
}

/** Resolve when every image of the frame has loaded or failed (a missing logo never blocks). */
function imagesSettled(fdoc) {
  const imgs = Array.from(fdoc?.images ?? []);
  return Promise.all(
    imgs.map((img) =>
      img.complete
        ? null
        : new Promise((res) => {
            img.addEventListener("load", res, { once: true });
            img.addEventListener("error", res, { once: true });
          }),
    ),
  );
}

/**
 * Print an HTML document from a hidden iframe (the print root). Resolves once the browser's print
 * dialog has been handed the document (in Chromium, after the dialog closes); rejects with
 * `PRINT_FRAME_FAILED` when the frame cannot be built.
 *
 * @param {string} html  a complete document (`buildPrintHtml`)
 * @param {{ document?: Document, setTimer?: Function, assetTimeoutMs?: number }} [deps]
 */
export async function printHtmlInFrame(html, deps = {}) {
  const doc = deps.document ?? (typeof document !== "undefined" ? document : null);
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  if (!doc?.body) throw printError("PRINT_FRAME_FAILED");
  let frame;
  try {
    frame = doc.createElement("iframe");
    frame.setAttribute("data-print-frame", "");
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("tabindex", "-1");
    frame.setAttribute("title", "Impression");
    // Not display:none — a frame that is not rendered does not print in every engine.
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    doc.body.appendChild(frame);
    const win = frame.contentWindow;
    const fdoc = frame.contentDocument ?? win?.document;
    if (!win || !fdoc) throw printError("PRINT_FRAME_FAILED");
    fdoc.open();
    fdoc.write(html);
    fdoc.close();

    const ms = deps.assetTimeoutMs ?? PRINT_FRAME.ASSET_TIMEOUT_MS;
    await withTimeout(Promise.all([fdoc.fonts?.ready, imagesSettled(fdoc)]), ms, setTimer);

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      frame.parentNode?.removeChild(frame);
    };
    win.addEventListener?.("afterprint", () => setTimer(remove, 0), { once: true });
    setTimer(remove, PRINT_FRAME.CLEANUP_FALLBACK_MS);
    win.focus?.();
    win.print();
    return { frame };
  } catch (err) {
    frame?.parentNode?.removeChild(frame);
    throw err?.code ? err : printError("PRINT_FRAME_FAILED", err);
  }
}

/**
 * Print a rendered `[data-print-doc]` node (or a container holding one): the one entry point the
 * components and `useCtrlP` use.
 *
 * @param {Element} node
 * @param {{ css?: string, title?: string, printedBy?: string, printedAt?: Date, document?: Document }} [opts]
 */
export function printDocNode(node, opts = {}) {
  const meta = readPrintDoc(node);
  const doc = opts.document ?? (typeof document !== "undefined" ? document : null);
  const html = buildPrintHtml({
    title: opts.title ?? meta.title,
    bodyHtml: meta.html,
    css: opts.css ?? "",
    fontCss: collectFontFaceCss(doc),
    pageKey: meta.pageKey,
    printedAt: opts.printedAt ?? new Date(),
    printedBy: opts.printedBy,
  });
  return printHtmlInFrame(html, { ...opts.frameDeps, document: doc });
}

/**
 * A print run guard: one print at a time (a held Ctrl+P or a double click never opens two
 * dialogs), « Préparation de l'impression… » announced only when preparing is slow, the failure
 * message on error. Framework-free so `useCtrlP` and the tests share it.
 *
 * @param {{ notify?: (msg: string, type?: string) => void, messages: { preparation: string, echec: string, enCours: string }, announceAfterMs?: number, setTimer?: Function, clearTimer?: Function, onChange?: (busy: boolean) => void }} deps
 */
export function createPrintRunner(deps) {
  const notify = deps.notify ?? (() => {});
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));
  const announceAfter = deps.announceAfterMs ?? PRINT_FRAME.ANNOUNCE_AFTER_MS;
  let busy = false;
  return {
    get busy() {
      return busy;
    },
    /** @param {() => unknown} run  @returns {Promise<"printed"|"busy"|"failed">} */
    async run(run) {
      if (busy) {
        notify(deps.messages.enCours, "info");
        return "busy";
      }
      busy = true;
      deps.onChange?.(true);
      const t = setTimer(() => notify(deps.messages.preparation, "info"), announceAfter);
      try {
        await run();
        return "printed";
      } catch (err) {
        if (typeof console !== "undefined") console.error("[print]", err);
        notify(deps.messages.echec, "error");
        return "failed";
      } finally {
        clearTimer(t);
        busy = false;
        deps.onChange?.(false);
      }
    },
  };
}
